import { areaOfSpot, calloutAt, calloutLabels, callouts, type CalloutEdit, type Level, type MapId } from "@69ly/maps";
import type { CalloutEdits, Stuff } from "@69ly/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "./api.ts";

const KEY = ["callouts"] as const;
const NONE: CalloutEdit[] = [];

/** The admins' callout changes for every map: loaded once, kept for the visit. */
export function useCalloutEdits() {
  return useQuery({ queryKey: KEY, queryFn: () => api<CalloutEdits>("GET", "/callouts"), staleTime: 5 * 60_000 });
}

export function useSaveCallouts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ map, edits }: { map: MapId; edits: CalloutEdit[] }) => api<CalloutEdits>("PUT", `/admin/callouts/${map}`, { edits }),
    onSuccess: (data, { map }) =>
      qc.setQueryData<CalloutEdits>(KEY, (old) => ({
        maps: { ...old?.maps, [map]: data.maps[map] ?? [] },
        updatedAt: { ...old?.updatedAt, ...data.updatedAt },
      })),
  });
}

/**
 * Callouts with the admins' changes applied: names on the radar, the callout
 * under a spot, where a stuff is thrown from and lands, and the area of a spot.
 */
export function useCallouts() {
  const { data } = useCalloutEdits();
  return useMemo(() => {
    const edits = (map: MapId): CalloutEdit[] => (data?.maps[map] as CalloutEdit[] | undefined) ?? NONE;
    return {
      edits,
      all: (map: MapId, level: Level) => callouts(map, level, edits(map)),
      labels: (map: MapId, level: Level) => calloutLabels(map, level, edits(map)),
      at: (map: MapId, x: number, y: number, z: number | null) => calloutAt(map, x, y, z, edits(map)),
      area: (map: MapId, x: number, y: number, z: number | null) => areaOfSpot(map, x, y, z, edits(map)),
      /** Where a stuff is thrown from and where it lands, as callouts ("T Spawn", "Jungle"). */
      places: (map: MapId, s: Pick<Stuff, "throw" | "landing">) => ({
        from: calloutAt(map, s.throw.x, s.throw.y, s.throw.z, edits(map)),
        to: calloutAt(map, s.landing.x, s.landing.y, s.landing.z ?? s.throw.z, edits(map)),
      }),
    };
  }, [data]);
}
